import { Button, styled } from '@mui/material';
import { ReactNode, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import Translate from '@/component/Translate';
import { useTranslation } from '@/feature/lang';
import { Icon } from '@/icon/Icon';
import { useDebounce } from '@/util/useDebounce';

interface ScrollToEndProps {
  children: ReactNode;
  onScrolledToEnd?: () => void;
}

const ContentContainer = styled('div', {
  name: 'ScrollToEnd',
  slot: 'ContentContainer',
})({ position: 'relative' });

const Sentinel = styled('div', {
  name: 'ScrollToEnd',
  slot: 'Sentinel',
})({ position: 'absolute', bottom: 0, height: '32px' });

const StickyFooter = styled('div', {
  name: 'ScrollToEnd',
  slot: 'StickyFooter',
})({ position: 'sticky', bottom: 0, left: 0 });

const ButtonContainer = styled('div', {
  name: 'ScrollToEnd',
  slot: 'ButtonContainer',
})({ position: 'relative', display: 'flex', justifyContent: 'center' });

const ScrollToEndButton = styled(Button, {
  name: 'ScrollToEnd',
  slot: 'ScrollToEndButton',
})({ position: 'absolute', bottom: 0 });

export const ScrollToEnd = ({
  children,
  onScrolledToEnd,
}: ScrollToEndProps) => {
  const { texts } = useTranslation();
  const { ref: sentinelRef, inView } = useInView({
    onChange: (isInView) => {
      if (isInView && onScrolledToEnd) {
        onScrolledToEnd();
      }
    },
  });
  const delay = useDebounce({ initial: true, next: false }, 1500);
  const [scrollInProgress, setScrollInProgress] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);
  const handleScrollToEnd = () => {
    if (scrollRef && scrollRef.current) {
      setScrollInProgress(true);
      scrollRef.current.scrollIntoView({ behavior: 'smooth' });
      setTimeout(() => setScrollInProgress(false), 3000);
    }
  };

  return (
    <>
      <ContentContainer>
        {children}
        {!delay && <Sentinel ref={sentinelRef}></Sentinel>}
        <div ref={scrollRef} />
      </ContentContainer>
      <StickyFooter>
        <ButtonContainer>
          {!inView && !delay && !scrollInProgress && (
            <ScrollToEndButton
              startIcon={<Icon name="expand_more" size="small" />}
              variant="contained"
              onClick={handleScrollToEnd}
            >
              <Translate text={texts.agreement.action.readMore} />
            </ScrollToEndButton>
          )}
        </ButtonContainer>
      </StickyFooter>
    </>
  );
};
